import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ListChecks, ChevronDown, ChevronUp, MessageSquare } from 'lucide-react';
import { ScenarioResultBadge } from './ScenarioResultBadge';

interface AoFeedbackItem {
	title: string;
	description?: string;
	pass?: boolean;
	items?: AoFeedbackItem[];
	pass_amount?: number;
}

/** pass가 없으면 자식으로 내려가, 명시적 false가 하나라도 있으면 실패 */
function isAoFeedbackItemFailed(item: AoFeedbackItem): boolean {
	if (item.pass === true) {
		return false;
	}
	if (item.pass === false) {
		return true;
	}
	if (!Array.isArray(item.items) || item.items.length === 0) {
		return false;
	}
	return item.items.some(isAoFeedbackItemFailed);
}

interface AoEvaluationData {
	patient_assessment?: AoFeedbackItem[];
	infection_control?: AoFeedbackItem[];
	communication?: AoFeedbackItem[];
	safety?: AoFeedbackItem[];
	procedural_competence?: AoFeedbackItem[];
}

interface AoEvaluationResultProps {
	resultMetadata: unknown;
}

const CATEGORY_LABELS: Record<string, string> = {
	patient_assessment: 'Patient Assessment',
	infection_control: 'Infection Control',
	communication: 'Communication',
	safety: 'Safety',
	procedural_competence: 'Procedural Competence',
};

const CATEGORY_ORDER = [
	'patient_assessment',
	'infection_control',
	'communication',
	'safety',
	'procedural_competence',
];

export default function AoEvaluationResult({ resultMetadata }: AoEvaluationResultProps): React.ReactElement {
	const { t: tScenario } = useTranslation('scenario');
	const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

	const m = resultMetadata as Record<string, unknown> | null | undefined;
	const aoEvaluation = m?.aoEvaluation as AoEvaluationData | undefined;

	const toggleRow = (rowId: string) => {
		setExpandedRows((prev) => {
			const next = new Set(prev);
			if (next.has(rowId)) {
				next.delete(rowId);
			} else {
				next.add(rowId);
			}
			return next;
		});
	};

	if (!aoEvaluation) {
		return (
			<div className="space-y-4 !mt-6 !mb-4">
				<h3 className="text-lg md:text-xl font-bold text-gray-900 flex items-center gap-2.5">
					<div className="w-9 h-9 flex items-center justify-center rounded-lg bg-slate-200/60 border border-slate-200/80">
						<ListChecks size={20} className="text-slate-600" />
					</div>
					수행 평가
				</h3>
				<p className="text-sm text-gray-500">평가 데이터가 없습니다.</p>
			</div>
		);
	}

	// 모든 카테고리의 항목을 하나의 배열로 변환
	const allRows: Array<{
		rowId: string;
		category: string;
		categoryLabel: string;
		categoryNumber: number;
		item: AoFeedbackItem;
		isFirstInCategory: boolean;
		categoryRowSpan: number;
	}> = [];

	let categoryIndex = 0;
	CATEGORY_ORDER.forEach((category) => {
		const items = aoEvaluation[category as keyof AoEvaluationData];
		if (Array.isArray(items) && items.length > 0) {
			categoryIndex++;
			items.forEach((item, idx) => {
				allRows.push({
					rowId: `${category}-${idx}`,
					category,
					categoryLabel: CATEGORY_LABELS[category] || category,
					categoryNumber: categoryIndex,
					item,
					isFirstInCategory: idx === 0,
					categoryRowSpan: items.length,
				});
			});
		}
	});

	if (allRows.length === 0) {
		return (
			<div className="space-y-4 !mt-6 !mb-4">
				<h3 className="text-lg md:text-xl font-bold text-gray-900 flex items-center gap-2.5">
					<div className="w-9 h-9 flex items-center justify-center rounded-lg bg-slate-200/60 border border-slate-200/80">
						<ListChecks size={20} className="text-slate-600" />
					</div>
					수행 평가
				</h3>
				<p className="text-sm text-gray-500">평가 항목이 없습니다.</p>
			</div>
		);
	}

	return (
		<div className="space-y-4 !mt-6 !mb-4">
			<h3 className="text-lg md:text-xl font-bold text-gray-900 flex items-center gap-2.5">
				<div className="w-9 h-9 flex items-center justify-center rounded-lg bg-slate-200/60 border border-slate-200/80">
					<ListChecks size={20} className="text-slate-600" />
				</div>
				수행 평가
			</h3>

			<div className="overflow-hidden rounded-xl border border-slate-200/90 shadow-sm bg-white">
				<table className="w-full text-base text-start border-collapse table-fixed">
					<thead className="text-sm text-gray-700 uppercase bg-gray-50 border-b border-gray-200">
						<tr>
							<th className="px-4 py-3 w-16 text-center">NO</th>
							<th className="px-4 py-3 w-40">평가항목</th>
							<th className="px-4 py-3">내용</th>
							<th className="px-4 py-3 w-20 text-center">결과</th>
							<th className="px-4 py-3 w-20 text-center">코멘트</th>
						</tr>
					</thead>
					<tbody className="divide-y divide-gray-200">
						{allRows.map((row, idx) => {
							const isExpanded = expandedRows.has(row.rowId);
							const hasComment = row.item.description || (row.item.items && row.item.items.length > 0);

							return (
								<React.Fragment key={row.rowId}>
									<tr
										className={`hover:bg-gray-50 transition-colors ${hasComment ? 'cursor-pointer' : ''} ${
											isExpanded ? 'bg-gray-50' : ''
										}`}
										onClick={() => hasComment && toggleRow(row.rowId)}
									>
										{row.isFirstInCategory && (
											<td
												className="px-4 py-3 text-center text-gray-500 font-medium bg-gray-50/50 border-r border-gray-200"
												rowSpan={row.categoryRowSpan}
											>
												{row.categoryNumber}
											</td>
										)}
										{row.isFirstInCategory && (
											<td
												className="px-4 py-3 font-medium text-gray-900 text-sm bg-gray-50/50 border-r border-gray-200"
												rowSpan={row.categoryRowSpan}
											>
												{row.categoryLabel}
											</td>
										)}
										<td className="px-4 py-3 text-sm text-gray-700">
											<div className="flex flex-col">
												<div className="leading-relaxed">{row.item.title}</div>
												{isExpanded && hasComment && (
													<div className="mt-3 pt-3 border-t border-gray-200 -mx-4 px-4 -mr-40">
														<div className="bg-white p-4 rounded-lg border border-gray-200">
															<div className="flex items-start gap-3">
																<div className="mt-0.5 p-1.5 bg-indigo-50 rounded-full text-indigo-600 flex-shrink-0">
																	<MessageSquare size={16} />
																</div>
																<div className="flex-1 min-w-0">
																	{row.item.description && (
																		<p className="text-gray-600 text-sm leading-relaxed whitespace-pre-line mb-3 break-words">
																			{row.item.description}
																		</p>
																	)}
																	{row.item.items && row.item.items.length > 0 && (
																		<div className="text-sm text-gray-700 break-words">
																			{row.item.items.map((subItem, subIdx) => (
																				<span key={subIdx}>
																					{subIdx > 0 && ', '}
																					<span className={isAoFeedbackItemFailed(subItem) ? 'text-red-600 font-medium' : ''}>
																						{subItem.title}
																					</span>
																				</span>
																			))}
																		</div>
																	)}
																</div>
															</div>
														</div>
													</div>
												)}
											</div>
										</td>
										<td className="px-4 py-3 text-center" style={{ verticalAlign: 'top' }}>
											<ScenarioResultBadge
												ok={row.item.pass === true}
												labelCorrect={tScenario('gcsEvaluationResult.resultCorrect')}
												labelWrong={tScenario('gcsEvaluationResult.resultWrong')}
											/>
										</td>
										<td className="px-4 text-center relative" style={{ verticalAlign: 'top' }}>
											<div className="absolute top-3 left-1/2 -translate-x-1/2 flex items-center" style={{ height: '24px' }}>
												{hasComment ? (
													<div
														className={`h-8 w-8 p-0 inline-flex items-center justify-center rounded-md transition-all cursor-pointer ${
															isExpanded ? 'bg-gray-200' : ''
														}`}
													>
														{isExpanded ? (
															<ChevronUp size={14} className="text-gray-600" />
														) : (
															<ChevronDown size={14} className="text-gray-500" />
														)}
													</div>
												) : (
													<span className="text-gray-300 text-sm">—</span>
												)}
											</div>
										</td>
									</tr>
								</React.Fragment>
							);
						})}
					</tbody>
				</table>
			</div>
		</div>
	);
}
